/**
 * Self-service password reset — the confirm half (Zone 1).
 *
 * Landing page for the emailed link (`/reset-password?token=…` — the path is
 * pinned by `AUTH_PASSWORD_RESET_URL_BASE`; token only, no email, so the
 * address never lands in browser history or proxy logs). New password twice →
 * `POST /auth/v1/password/reset/confirm`. A used/expired link gets an explicit
 * dead-end with a "request a new link" escape hatch, because that is the one
 * thing the person can actually do about it.
 *
 * Staff invites land here too (`?token=…&invite=1&team=<slug>`): an invite is
 * a first-password reset token minted by users-service, so only the copy
 * changes. `team` points the success link at that team's sign-in.
 */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { TextField } from '../../components/text-field';
import { ApiError } from '../../lib/api-error';
import { ROUTES } from '../../lib/constants';

import { validatePassword } from './signup-validation';

export function ResetPasswordPage(): JSX.Element {
  const apiClient = useApiClient();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token') ?? '';
  const isInvite = searchParams.get('invite') === '1';
  const team = searchParams.get('team');
  const loginHref =
    team !== null && team !== ''
      ? `${ROUTES.login}?team=${encodeURIComponent(team)}`
      : ROUTES.login;

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [isDone, setIsDone] = useState(false);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const errors: Record<string, string> = {};
    const passwordError = validatePassword(password);
    if (passwordError !== null) errors.password = passwordError;
    if (confirm !== password) errors.confirm = 'Passwords do not match.';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setFormError(null);
    setIsBusy(true);
    try {
      await apiClient.confirmPasswordReset({ token, newPassword: password });
      setPassword('');
      setConfirm('');
      setIsDone(true);
    } catch (error) {
      setFormError(toResetMessage(error, isInvite));
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <div className="auth">
      <Link to={ROUTES.landing} className="auth__brand">
        <img src="/images/logo-mark.png" alt="" width={32} height={32} />
        <span>
          GRIDIRON <em>TRACKER</em>
        </span>
      </Link>

      <main className="auth__panel">
        {token === '' ? (
          <div className="stack">
            <h1 className="auth__title">
              {isInvite ? 'This invite link is incomplete' : 'This reset link is incomplete'}
            </h1>
            <div className="banner banner--error" role="alert">
              <span className="banner__title">Missing reset token</span>
              <span>
                The link is missing its token — it may have been cut off by your email client. Open
                the link directly from the email, or request a new one.
              </span>
            </div>
            <Link className="btn btn--primary btn--block" to={ROUTES.forgotPassword}>
              Request a new reset link
            </Link>
          </div>
        ) : isDone ? (
          <div className="stack">
            <h1 className="auth__title">{isInvite ? 'Account ready' : 'Password updated'}</h1>
            <div className="banner" role="status">
              <span className="banner__title">You’re all set</span>
              <span>
                {isInvite
                  ? 'Your password is set. Sign in to start using Gridiron Tracker.'
                  : 'Your password has been changed and every other device has been signed out. Sign in with the new password to continue.'}
              </span>
            </div>
            <Link className="btn btn--primary btn--block" to={loginHref}>
              Sign in
            </Link>
          </div>
        ) : (
          <form className="stack" onSubmit={(event) => void onSubmit(event)} noValidate>
            <div>
              <h1 className="auth__title">
                {isInvite ? 'Set your password' : 'Choose a new password'}
              </h1>
              <p className="muted">
                {isInvite
                  ? 'You were invited to a team on Gridiron Tracker. Choose a password to finish setting up your account.'
                  : 'This signs out every other device where you are logged in.'}
              </p>
            </div>
            {formError ? (
              <div className="banner banner--error" role="alert">
                <span className="banner__title">
                  {isInvite ? 'Could not set the password' : 'Could not reset the password'}
                </span>
                <span>{formError}</span>
              </div>
            ) : null}
            <TextField
              label="New password"
              name="new_password"
              type="password"
              required
              autoFocus
              autoComplete="new-password"
              value={password}
              error={fieldErrors.password ?? null}
              onChange={(event) => setPassword(event.target.value)}
            />
            <TextField
              label="Confirm new password"
              name="confirm_password"
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              error={fieldErrors.confirm ?? null}
              onChange={(event) => setConfirm(event.target.value)}
            />
            <button type="submit" className="btn btn--primary btn--block" disabled={isBusy}>
              {isBusy ? 'Saving…' : isInvite ? 'Set password' : 'Set new password'}
            </button>
            <p className="field__hint">
              {isInvite ? (
                'Link expired? Ask your Team Admin to send a new invite.'
              ) : (
                <>
                  Link expired? <Link to={ROUTES.forgotPassword}>Request a new one</Link>
                </>
              )}
            </p>
          </form>
        )}
      </main>
    </div>
  );
}

/** Turn a confirm failure into copy the person can act on. */
function toResetMessage(error: unknown, isInvite: boolean): string {
  if (!ApiError.isApiError(error)) return 'Could not set the password. Please try again.';
  if (error.isOffline) return error.message;
  const expired =
    error.status === 410 || (error.status === 400 && error.code === 'INVALID_RESET_TOKEN');
  if (expired) {
    return isInvite
      ? 'This invite link has already been used or has expired. Ask your Team Admin to send a new invite.'
      : 'This reset link has already been used or has expired. Request a new one below.';
  }
  // Password policy failures (complexity / reuse / breach) carry server copy.
  return error.message;
}
